import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PostSource, PostStatus } from '../../data-layer/posts/constants';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { Dialog, DialogContent, DialogTitle } from '../../ui/feedback/Dialog';
import { WithHint } from '../../ui/feedback/Tooltip';
import { Stack } from '../../ui/layout/Stack';
import { DraftSelectionRow, DraftSelectionRowSkeleton } from '../../ui/posts/DraftSelectionRow';
import { Typography } from '../../ui/typography/Typography';
import { useTranslation } from '../../utils/useTranslation';
import { usePostCollection } from './usePostCollection';

const SKELETON_COUNT = 5;
const DEFAULT_HORIZONTAL_GAP = { xs: 2, sm: 3 };

interface DraftSelectionDialogProps {
    businessId: string;
    onClose: VoidFunction;
}

export const DraftSelectionDialog = (props: DraftSelectionDialogProps) => {
    const { businessId, onClose } = props;
    const t = useTranslation();
    const collection = usePostCollection({
        namespace: `persistence-drafts-${businessId}`,
        filters: {
            amount: 10,
            businessId: props.businessId,
            statuses: [PostStatus.DRAFT],
        },
    });
    const navigate = useNavigate();
    const onSelect = useCallback(
        (postId: string) => {
            onClose();
            navigate(`/posts/${postId}/edit`, { replace: true });
        },
        [navigate, onClose],
    );

    const shouldDisplayDisclaimer = Boolean(
        collection.items.find((post) => post.source === PostSource.INSTAGRAM),
    );

    return (
        <Dialog onClose={onClose}>
            <DialogTitle onClose={onClose}>
                <WithHint
                    message={
                        'Отображаются последние 10 черновиков. Чтобы посмотреть все перейдите во вкладку "Управление лентой"'
                    }
                >
                    <Typography variant="title3" weight="bold">
                        {t('Черновики')}
                    </Typography>
                </WithHint>
            </DialogTitle>
            <DialogContent py={0} px={0} pb={1.5} minHeight={300}>
                {collection.isLoading || collection.items.length > 0 ? (
                    <>
                        <Stack
                            width="100%"
                            count={collection.isLoading ? SKELETON_COUNT : collection.items.length}
                            renderItem={(index) => {
                                const post = collection.items[index];

                                if (!post) {
                                    return <DraftSelectionRowSkeleton key={index} />;
                                }

                                return (
                                    <DraftSelectionRow
                                        key={post.id}
                                        post={toPostUI(post)}
                                        onClick={() => onSelect(post.id)}
                                    />
                                );
                            }}
                        />
                        {shouldDisplayDisclaimer ? (
                            <Typography
                                px={DEFAULT_HORIZONTAL_GAP}
                                variant="caption"
                                color="secondary"
                                mt="auto"
                            >
                                {t(
                                    '*Meta, владеющая Instagram, признана экстремистской организацией на территории РФ',
                                )}
                            </Typography>
                        ) : null}
                    </>
                ) : (
                    <Typography
                        variant="body"
                        px={DEFAULT_HORIZONTAL_GAP}
                        color="secondary"
                        m="auto"
                    >
                        {t('У вас нет созданных черновиков 👀')}
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};
